'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const testimonials = [
{
  quote: "I described my Google Ads problem at 11pm on a Tuesday. By 11:08pm I was on a screen share with a Genie who had already spotted the issue. By midnight, my campaign was profitable. Nothing else works like this.",
  name: 'Marcus Webb',
  role: 'Founder, DTC Brand',
  company: 'Ember & Oak',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18871df62-1763298878919.png",
  avatarAlt: 'Marcus Webb, founder with short dark hair smiling confidently',
  metric: '$12K saved',
  metricLabel: 'in wasted ad spend',
  color: '#FF3008',
  delay: '0s'
},
{
  quote: "We run a 20-person agency. Summons replaced our entire freelancer bench for overflow work. The escrow model means our clients never pay for bad work. It\'s the infrastructure we didn\'t know we needed.",
  name: 'Priya Nair',
  role: 'CEO',
  company: 'Velocity Digital Agency',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1965c6713-1763299935220.png",
  avatarAlt: 'Priya Nair, professional woman with dark hair in business attire',
  metric: '3x faster',
  metricLabel: 'client delivery speed',
  color: '#6C5CE7',
  delay: '0.1s'
},
{
  quote: "As a Genie, I made more in my first week than in a month on Upwork. The instant payout is real — I approve the session, money's in my account. The platform treats experts like professionals, not commodities.",
  name: 'Jordan Kim',
  role: 'Email Marketing Genie',
  company: 'Top-rated · 4.97★',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_176c4fa3c-1763296193395.png",
  avatarAlt: 'Jordan Kim, young professional with casual confident expression',
  metric: '4.97★',
  metricLabel: 'average rating',
  color: '#FFB800',
  delay: '0.2s'
}];


export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 lg:py-28 px-4 sm:px-6 bg-surface">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 reveal">
          <p className="text-xs font-bold uppercase tracking-widest text-muted mb-3">Real Results</p>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground tracking-tight">
            Trusted by Founders
            <br />
            <span className="text-[rgba(255,48,8,1)]">and Genies Alike.</span>
          </h2>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials?.map((t, i) =>
          <div
            key={i}
            className="reveal-scale testimonial-card bg-white rounded-3xl p-7 shadow-card border border-border flex flex-col justify-between relative overflow-hidden"
            style={{ transitionDelay: t?.delay }}>
            
              {/* Top accent */}
              <div
              className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl"
              style={{ background: t?.color }} />
            

              {/* Quote */}
              <div>
                <div className="text-3xl mb-4 opacity-20" style={{ color: t?.color }}>"</div>
                <p className="text-sm text-gray-700 leading-relaxed mb-6 italic">"{t?.quote}"</p>
              </div>

              {/* Author */}
              <div>
                {/* Metric */}
                <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold mb-5"
                style={{ background: `${t?.color}15`, color: t?.color }}>
                
                  <span className="text-base font-black">{t?.metric}</span>
                  <span className="font-medium opacity-70">{t?.metricLabel}</span>
                </div>

                <div className="flex items-center gap-3">
                  <AppImage
                  src={t?.avatar}
                  alt={t?.avatarAlt}
                  width={44}
                  height={44}
                  className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                
                  <div>
                    <p className="text-sm font-bold text-foreground">{t?.name}</p>
                    <p className="text-xs text-muted">{t?.role} · {t?.company}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Join Waitlist CTA after testimonials */}
        <div className="mt-14 text-center reveal" style={{ transitionDelay: '0.3s' }}>
          <Link
            href="/waitlist"
            className="inline-flex items-center justify-center gap-2 bg-primary text-white px-10 py-4 rounded-2xl font-bold text-base hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
          >
            <span>Join the Waitlist</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </Link>
          <p className="mt-3 text-sm text-muted">Be first in line when we launch. No spam, ever.</p>
        </div>
      </div>
    </section>
  );
}